import type { WorksheetAssignee } from '@mathflat/domain/Worksheet'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'

import { buildStudentScoringPath } from '@/page/QrScoring/_utils/roster'
import commonRepo from '@/repository/common.repo'
import { CustomEventService } from '@/service/customEvent.service'
import { isPhoneDevice } from '@/service/viewport.service'

import { fetchQrScoringTarget } from './_utils/fetchQrScoringTarget'
import { QR_REJECT_MESSAGE } from './_utils/qrScoringTarget'
import {
  buildQrScoringPath,
  parseWorksheetQrQuery,
  type WorksheetQrQuery,
} from './_utils/worksheetQr'
import QrCameraLayer from './QrCamera/QrCameraLayer'
import { openQrRejectModal } from './QrRejectNotice'
import QrRosterModal from './QrRosterModal/QrRosterModal'
import { getEntryMode, qrScanStore } from './qrScanStore'

/**
 * qrScanStore.start 로 연 카메라 레이어와 구버전 앱 네이티브 스캐너 결과를 받는 자리.
 * 스캔을 시작할 수 있는 화면마다 하나씩 둔다(GNB, 헤더 없는 /qr)
 */
const QrScanLayer = () => {
  const history = useHistory()
  const { pathname } = useLocation()

  // 뒤로가기 등으로 화면이 바뀌거나 이 자리가 사라지면 카메라도 닫는다
  useEffect(() => qrScanStore.closeCamera, [pathname])

  useEffect(() => {
    if (getEntryMode() !== 'native-scanner') return
    return CustomEventService.onQRCodeReceived.listen(({ detail }) => {
      const query = parseWorksheetQrQuery(new URLSearchParams(detail).toString())
      if (query) {
        history.push(buildQrScoringPath(query))
        return
      }
      openQrRejectModal({ message: QR_REJECT_MESSAGE['not-worksheet'], actionButtonText: '확인' })
    })
  }, [])

  const [roster, setRoster] = useState<{ query: WorksheetQrQuery; resume: () => void } | null>(null)
  useEffect(() => {
    if (!qrScanStore.isCameraOpen) setRoster(null)
  }, [qrScanStore.isCameraOpen])

  const openStudentScoring = (assignee: WorksheetAssignee) => {
    qrScanStore.closeCamera()
    history.push(buildStudentScoringPath(assignee))
  }

  const onDetect = (query: WorksheetQrQuery, resume: () => void) => {
    // 휴대폰과 수업 권한 거절은 /qr 이 맡는다
    if (isPhoneDevice || !commonRepo.permission.get.lesson.access) {
      qrScanStore.closeCamera()
      history.push(buildQrScoringPath(query))
      return
    }
    // 태블릿·PC 는 카메라 위에서 끝낸다. 공통 QR 은 학생을 고르게 하고, 학생별 QR 은 학생 선택과 같은 길로
    // 그 학생 채점을 연다 — /qr 을 거쳐 넘어가면 수업 화면이 그 학생을 열지 못하는 경우가 있었다
    const isCommonQr = query.studentWorksheetId === undefined && query.studentId === undefined
    if (isCommonQr) {
      setRoster({ query, resume })
      return
    }
    void fetchQrScoringTarget(query).then((target) => {
      // 조회하는 사이 카메라를 닫았으면 결과를 버린다
      if (!qrScanStore.isCameraOpen) return
      if (target.type === 'student') {
        openStudentScoring(target.assignee)
        return
      }
      const message =
        target.type === 'reject'
          ? QR_REJECT_MESSAGE[target.reason]
          : '학습지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요'
      openQrRejectModal({ message, onClose: resume })
    })
  }

  if (!qrScanStore.isCameraOpen) return null
  return (
    <>
      <QrCameraLayer onClose={qrScanStore.closeCamera} onDetect={onDetect} isSuspended={!!roster} />
      {roster && (
        <QrRosterModal
          query={roster.query}
          onDismiss={() => {
            setRoster(null)
            roster.resume()
          }}
          onPick={openStudentScoring}
        />
      )}
    </>
  )
}

export default observer(QrScanLayer)
